import { useMemo, useState } from "react";
import { confirmDialog } from "../components/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Pencil, Plus, Trash2, Eye, EyeOff, Sparkles, Search, BookOpen, GraduationCap, Video, Lightbulb, AlertTriangle } from "lucide-react";
import AdminCoursesTabs from "../components/AdminCoursesTabs";

const CAT_LABEL = { article: "Rétrospective", concept: "Concept", vocabulary: "Glossaire", video: "Vidéo" };
const CAT_ICON = { article: BookOpen, concept: Lightbulb, vocabulary: GraduationCap, video: Video };

export default function AdminCoursesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });

  const { data: themes = [] } = useQuery({
    queryKey: ["course-themes"],
    queryFn: async () => (await api.get("/course-themes")).data,
  });
  const themeTitle = Object.fromEntries(themes.map((t) => [t.id, t.title]));
  // À compléter : concepts sans thématique, rétrospectives sans année
  const todo = {
    noTheme: courses.filter((c) => c.category === "concept" && !c.theme_id).length,
    noPeriod: courses.filter((c) => c.category === "article" && typeof c.year_start !== "number").length,
  };

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/courses/${id}`)).data,
    onSuccess: () => {
      toast.success("Cours supprimé");
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const startCreate = () => navigate("/admin/cours/nouveau");
  const startEdit = (c) => navigate(`/admin/cours/${c.id}/modifier`);

  const counts = useMemo(() => ({
    all: courses.length,
    article: courses.filter((c) => c.category === "article").length,
    concept: courses.filter((c) => c.category === "concept").length,
    vocabulary: courses.filter((c) => c.category === "vocabulary").length,
    drafts: courses.filter((c) => c.published === false).length,
  }), [courses]);

  const FILTERS = [
    { id: "all", label: "Tout" },
    { id: "article", label: "Rétrospective" },
    { id: "concept", label: "Concept" },
    { id: "vocabulary", label: "Glossaire" },
    { id: "drafts", label: "Brouillons" },
  ];

  const filtered = useMemo(() => {
    let list = [...courses].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    if (filter === "drafts") list = list.filter((c) => c.published === false);
    else if (filter !== "all") list = list.filter((c) => c.category === filter);
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((c) =>
        c.title.toLowerCase().includes(s) ||
        (c.description || "").toLowerCase().includes(s) ||
        (c.tags || []).some((t) => t.toLowerCase().includes(s)),
      );
    }
    return list;
  }, [courses, filter, q]);

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
          <h1 className="font-display font-black text-3xl">Cours</h1>
        </div>
        <Button
          onClick={startCreate}
          className="bg-emerald-500 text-black hover:bg-emerald-300"
          data-testid="admin-courses-new"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nouveau cours
        </Button>
      </div>

      <AdminCoursesTabs />
      {(todo.noTheme > 0 || todo.noPeriod > 0) && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm" data-testid="admin-courses-todo">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-zinc-300">
            {todo.noTheme > 0 && <div><b className="text-amber-200">{todo.noTheme} concept{todo.noTheme > 1 ? "s" : ""} sans thématique</b> : affiché{todo.noTheme > 1 ? "s" : ""} dans « Autres concepts » sur le site. Rangez-les depuis l'onglet Thématiques.</div>}
            {todo.noPeriod > 0 && <div><b className="text-amber-200">{todo.noPeriod} rétrospective{todo.noPeriod > 1 ? "s" : ""} sans année</b> : placée{todo.noPeriod > 1 ? "s" : ""} en fin de frise. Ajoutez l'année dans l'éditeur.</div>}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="admin-courses-search"
            placeholder="Rechercher un cours, un mot-clé…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white/[0.02] border-white/10"
          />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="admin-courses-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`admin-courses-filter-${f.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.15em] border transition-colors ${
                filter === f.id
                  ? "bg-emerald-500 text-black border-emerald-500"
                  : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
              }`}
            >
              {f.label} <span className="opacity-70">· {counts[f.id] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center bg-card border-white/5">
          <p className="text-zinc-500">Aucun cours pour l'instant.</p>
          <Button className="mt-4 bg-emerald-500 text-black hover:bg-emerald-300" onClick={startCreate}>
            <Plus className="w-4 h-4 mr-1.5" /> Créer le premier cours
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="admin-courses-list">
          {filtered.map((c) => {
            const Icon = CAT_ICON[c.category] || BookOpen;
            const accessLabel = c.has_preview_break
              ? "✂ coupure manuelle"
              : c.is_premium
                ? "aperçu : aucun"
                : "Gratuit";
            return (
              <Card key={c.id} className="p-4 bg-card border-white/5 flex items-center gap-4" data-testid={`admin-course-row-${c.slug}`}>
                <div className="w-24 h-14 shrink-0 rounded-md overflow-hidden border border-white/5 bg-white/[0.02] grid place-items-center">
                  {c.category === "article" && c.thumbnail ? (
                    <img src={c.thumbnail} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Icon className="w-5 h-5 text-zinc-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium truncate">{c.title}</h3>
                    {c.is_premium && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] uppercase tracking-[0.15em]">
                        <Sparkles className="w-3 h-3" /> Essentiel
                      </span>
                    )}
                    {c.published === false && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-400 text-[10px] uppercase tracking-[0.15em]">
                        <EyeOff className="w-3 h-3" /> Brouillon
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 truncate">
                    {CAT_LABEL[c.category] || c.category}
                    {c.category === "concept" && <> · {c.theme_id ? themeTitle[c.theme_id] || "Thématique" : <span className="text-amber-300">sans thématique</span>}</>}
                    {c.category === "article" && <> · {typeof c.year_start === "number" ? c.period : <span className="text-amber-300">{c.period ? `« ${c.period} » : année à préciser` : "sans année"}</span>}</>}
                    {" "}· /cours/{c.slug} · {accessLabel}
                    {c.tags && c.tags.length > 0 && <> · {c.tags.join(", ")}</>}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => window.open(`/cours/${c.slug}`, "_blank")} data-testid={`course-view-${c.slug}`}>
                  <Eye className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => startEdit(c)} data-testid={`course-edit-${c.slug}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-rose-400 hover:bg-rose-500/10"
                  onClick={async () => {
                    if (await confirmDialog({ title: `Supprimer « ${c.title} » ?`, description: "Le cours disparaît du site, ainsi que les liens vers lui. Action définitive." })) deleteMut.mutate(c.id);
                  }}
                  data-testid={`course-delete-${c.slug}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
